import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, ClipboardCheck, LogOut, DownloadCloud } from 'lucide-react'
import { api } from '../../lib/api.js'
import { syncInformation, offlineReady, lastSyncedAt } from '../../lib/offlineSync.js'
import { fmtDateTime } from '../../lib/date.js'
import { toast } from '../../components/feedback/Toast.jsx'
import SpaceManager from './SpaceManager.jsx'
import TeamManager from './TeamManager.jsx'
import { NotificationsCard } from '../../components/PushSettings.jsx'

/** Everything that doesn't earn a tab: remaining spaces, account. (Logins got its own tab 2026-09-29.) */
export default function MorePage() {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const spaces = useQuery({ queryKey: ['spaces'], queryFn: () => api('/spaces') })

  const extra = (spaces.data?.spaces || []).filter(
    (s) => !['clients', 'studios', 'guides', 'logins'].includes(s.slug)
  )
  const user = me.data?.user
  const canEdit = ['admin', 'editor'].includes(user?.role)
  const isAdmin = user?.role === 'admin'

  // The admin accountability check (see /log/check). Shares its cache with
  // the Session Log home row and the tab-bar dot.
  const check = useQuery({
    queryKey: ['sl-check'],
    queryFn: () => api('/log/check'),
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  })
  const checkCount = (check.data?.days || []).reduce((n, d) => n + d.deficit, 0)

  async function logout() {
    await api('/auth/logout', { method: 'POST' }).catch(() => {})
    qc.setQueryData(['me'], null)
  }

  return (
    <div className="space-y-6">
      <h1 className="border-b border-hairline pb-3 text-2xl font-semibold text-ink">More</h1>

      <section>
        <div className="card divide-y divide-hairline">
          {isAdmin && (
            <Row
              to="/log/check"
              icon={
                <span className="relative">
                  <ClipboardCheck size={17} className="text-faint" />
                  {checkCount > 0 && (
                    <span className="absolute -right-1 -top-0.5 h-2 w-2 rounded-full bg-danger" />
                  )}
                </span>
              }
              label="Log check"
              hint={checkCount > 0 ? `${checkCount} to review` : undefined}
            />
          )}
          {extra.map((s) => (
            <Row
              key={s.slug}
              to={`/${s.slug}`}
              icon={<span className="w-[17px] text-center leading-none grayscale">{s.icon}</span>}
              label={s.name}
              hint={s.pageCount ? String(s.pageCount) : undefined}
            />
          ))}
        </div>
      </section>

      <OfflineSync />

      <NotificationsCard />

      {canEdit && <SpaceManager />}

      {user?.role === 'admin' && <TeamManager />}

      <section>
        <h2 className="section-header mb-2">Account</h2>
        <div className="card divide-y divide-hairline">
          <div className="px-4 py-3">
            <div className="text-sm font-medium text-ink">{user?.name}</div>
            <div className="text-xs text-muted">{user?.email} · {user?.role}</div>
          </div>
          <ChangePassword />
          <button onClick={logout} className="tap flex w-full items-center gap-2 px-4 py-3 text-sm text-danger active:bg-grouped">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </section>
    </div>
  )
}

/**
 * Sync-to-phone for CDM Information. Same walk the shell runs silently on
 * launch (lib/offlineSync.js) — this button is the loud, on-demand version.
 * Needs the deployed HTTPS site; the service worker can't run on the LAN
 * dev address.
 */
function OfflineSync() {
  const [state, setState] = useState('idle') // idle | syncing | done
  const [count, setCount] = useState(0)

  async function syncNow() {
    if (!offlineReady()) {
      toast('Offline sync activates on the deployed site (HTTPS)', 'error')
      return
    }
    setState('syncing')
    try {
      const n = await syncInformation(setCount)
      setCount(n)
      setState('done')
      toast('CDM Information synced for offline')
    } catch {
      setState('idle')
      toast('Sync failed — are you online?', 'error')
    }
  }

  const last = lastSyncedAt()

  return (
    <section>
      <h2 className="section-header mb-2">Offline</h2>
      <div className="card px-4 py-3">
        <button
          onClick={syncNow}
          disabled={state === 'syncing'}
          className="tap flex w-full items-center gap-2 text-sm font-medium text-ink disabled:opacity-60"
        >
          <DownloadCloud size={17} className="text-muted" />
          {state === 'syncing'
            ? `Syncing… ${count} pages`
            : state === 'done'
              ? `Synced — ${count} pages readable offline`
              : 'Sync CDM Information to this phone'}
        </button>
        <p className="mt-1.5 text-xs text-faint">
          Pages, client specs and login labels become readable without a connection.
          Refreshes itself each time the app opens (when older than 6 h).
          Passwords stay on the server; the Session Log stays online-only.
          {last > 0 && ` Last synced ${fmtDateTime(new Date(last).toISOString())}.`}
        </p>
      </div>
    </section>
  )
}

function Row({ to, icon, label, hint }) {
  return (
    <Link to={to} className="flex items-center gap-3 px-4 py-3 active:bg-grouped">
      {icon}
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{label}</span>
      {hint && <span className="text-xs text-faint">{hint}</span>}
      <ChevronRight size={16} className="shrink-0 text-faint" />
    </Link>
  )
}

function ChangePassword() {
  const [show, setShow] = useState(false)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [busy, setBusy] = useState(false)

  if (!show) {
    return (
      <button onClick={() => setShow(true)} className="tap flex w-full items-center px-4 py-3 text-sm text-ink active:bg-grouped">
        Change password
      </button>
    )
  }

  async function submit(e) {
    e.preventDefault()
    setBusy(true)
    try {
      await api('/auth/password', { method: 'PATCH', body: { current, next } })
      toast('Password changed')
      setShow(false)
      setCurrent('')
      setNext('')
    } catch (err) {
      toast(err.message || 'Couldn’t change password', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-2 px-4 py-3">
      <input
        type="password"
        required
        placeholder="Current password"
        autoComplete="current-password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        className="tap w-full rounded-xl border border-hairline bg-grouped px-3 py-2.5 text-[16px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-link"
      />
      <input
        type="password"
        required
        minLength={6}
        placeholder="New password (6+ chars, a capital and a number)"
        autoComplete="new-password"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        className="tap w-full rounded-xl border border-hairline bg-grouped px-3 py-2.5 text-[16px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-link"
      />
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="tap rounded-xl bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={() => setShow(false)} className="tap rounded-xl px-4 py-2 text-sm text-muted">
          Cancel
        </button>
      </div>
    </form>
  )
}
